import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import DropdownList from './DropdownList';
import { formatTime } from '../../utils/calendarDates';

/**
 * Time combobox for the event editor (owner request, 30.08): a dropdown of
 * 15-minute intervals in the user's 12h/24h format PLUS free typing that
 * accepts any time ("9", "9:3", "930", "9:30 pm"), committed on Enter/blur.
 * Garbage reverts to the previous value — never a silent 00:00.
 *
 * `ui/SearchableSelect` was checked first (the reuse rule): it is a
 * filter-SELECT — onChange only fires for an existing option, so it cannot
 * commit an arbitrary typed time. This follows its dropdown/outside-click
 * shape but keeps the input permanent, and wears the `modal-input` frame so
 * the border/focus states match the other editor fields exactly in both
 * themes (owner's explicit ask — no bespoke input skin).
 *
 * Values on the wire are 'HH:MM:SS' local wall-clock strings (the editor's
 * composeLocal contract); display is the timeFormat preference.
 */

export const TIME_STEP_MINUTES = 15;

/** 'HH:MM:SS' for a minutes-of-day value. */
export function minutesToTimeKey(minutes) {
  const m = ((minutes % 1440) + 1440) % 1440;
  const p = (n) => String(n).padStart(2, '0');
  return `${p(Math.floor(m / 60))}:${p(m % 60)}:00`;
}

export function timeKeyToMinutes(timeKey) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(timeKey || ''));
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

/** Display label for a 'HH:MM:SS' key in the user's format (label only). */
export function formatTimeKey(timeKey, timeFormat) {
  const mins = timeKeyToMinutes(timeKey);
  if (mins == null) return '';
  const d = new Date(2026, 0, 15); // any DST-quiet local date — label math only
  d.setHours(Math.floor(mins / 60), mins % 60, 0, 0);
  return formatTime(d, timeFormat);
}

/** The 96 15-minute options: [{ key: 'HH:MM:SS', minutes }]. */
export function timeOptions() {
  return Array.from({ length: (24 * 60) / TIME_STEP_MINUTES }, (_, i) => {
    const minutes = i * TIME_STEP_MINUTES;
    return { key: minutesToTimeKey(minutes), minutes };
  });
}

/**
 * Google-style duration hint for END options: "(30 min)", "(1 hr)", "(1.5 hr)".
 * A pure helper, so it reads the i18n singleton directly (the
 * utils/formatHistoryTime.js precedent) rather than taking a `t` argument.
 */
export function durationHint(startMinutes, optionMinutes) {
  if (startMinutes == null) return '';
  const diff = optionMinutes - startMinutes;
  if (diff <= 0 || diff > 24 * 60) return '';
  if (diff < 60) return i18n.t('calendar.editor.durationMin', { n: diff });
  const hrs = diff / 60;
  return i18n.t('calendar.editor.durationHr', {
    n: Number.isInteger(hrs) ? hrs : hrs.toFixed(1).replace(/\.0$/, ''),
  });
}

/**
 * Liberal time parse → 'HH:MM:SS' | null (null = garbage, caller reverts).
 * Accepted: "9" → 09:00 · "9:3" → 09:30 (single minute digit is the TENS
 * digit, the autocomplete convention) · "930"/"0930" → 09:30 · "9.30" ·
 * "9:30 pm"/"9pm"/"9 p" (am/pm work in both formats) · "14:05". Rejected:
 * hours > 23 (unless am/pm makes them 1–12), minutes > 59, anything else.
 */
export function parseTimeInput(raw, timeFormat) {
  const cleaned = String(raw ?? '')
    .trim()
    .toLowerCase()
    .replace(/\./g, ':')
    .replace(/\s+/g, ' ');
  if (!cleaned) return null;
  const m = /^(\d{1,4})(?::(\d{1,2}))?\s*(a|am|p|pm)?$/.exec(cleaned.replace(/\s*(a|am|p|pm)$/, ' $1').trim());
  if (!m) return null;
  let hours;
  let minutes;
  const digits = m[1];
  if (m[2] != null) {
    if (digits.length > 2) return null; // "123:45" is garbage
    hours = Number(digits);
    minutes = m[2].length === 1 ? Number(m[2]) * 10 : Number(m[2]);
  } else if (digits.length <= 2) {
    hours = Number(digits);
    minutes = 0;
  } else {
    // "930" / "0930": last two digits are the minutes.
    hours = Number(digits.slice(0, -2));
    minutes = Number(digits.slice(-2));
  }
  const meridiem = m[3] ? m[3][0] : null; // 'a' | 'p' | null
  if (meridiem) {
    if (hours < 1 || hours > 12) return null;
    if (meridiem === 'p' && hours !== 12) hours += 12;
    if (meridiem === 'a' && hours === 12) hours = 0;
  }
  if (hours > 23 || minutes > 59) return null;
  return minutesToTimeKey(hours * 60 + minutes);
}

export default function TimeCombobox({
  value, // 'HH:MM:SS'
  timeFormat,
  onCommit, // (timeKey) => void
  ariaLabel,
  startMinutes = null, // set on the END field → duration hints
  minMinutes = null, // owner refinement: END on the start's date lists only
  // times STRICTLY AFTER the start (start + 15 min onward); a later end DATE
  // passes null and gets the full 00:00–23:45 list. Free-typed earlier values
  // still commit here — the editor's end<start clamp keeps its job.
}) {
  const { t } = useTranslation();
  const [text, setText] = useState(() => formatTimeKey(value, timeFormat));
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const inputRef = useRef(null);
  const listId = useId();
  const committedRef = useRef(value);

  // External value / format changes re-seed the display text.
  useEffect(() => {
    committedRef.current = value;
    setText(formatTimeKey(value, timeFormat));
  }, [value, timeFormat]);

  const options = timeOptions().filter((o) => minMinutes == null || o.minutes > minMinutes);
  // Typing filters the list (label prefix/substring); an empty filter shows all.
  const filter = text.trim().toLowerCase();
  const showAll = !open || formatTimeKey(committedRef.current, timeFormat).toLowerCase() === filter;
  const visible = showAll
    ? options
    : options.filter((o) => formatTimeKey(o.key, timeFormat).toLowerCase().includes(filter));

  const commit = (timeKey) => {
    if (timeKey == null) {
      // Garbage: revert to the previous value, never a silent 00:00.
      setText(formatTimeKey(committedRef.current, timeFormat));
      return;
    }
    committedRef.current = timeKey;
    setText(formatTimeKey(timeKey, timeFormat));
    onCommit?.(timeKey);
  };

  const openList = () => {
    const r = inputRef.current?.getBoundingClientRect();
    if (r) setRect(r);
    setOpen(true);
  };

  return (
    <>
      <input
        ref={inputRef}
        className="modal-input"
        style={{ width: 110 }}
        data-editor-dropdown="true"
        role="combobox"
        aria-expanded={open}
        // Only while the list exists (it renders on open) — see EditorSelect.
        aria-controls={open && rect ? listId : undefined}
        aria-label={ariaLabel}
        value={text}
        onFocus={openList}
        onClick={openList}
        onChange={(e) => {
          setText(e.target.value);
          if (!open) openList();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit(parseTimeInput(text, timeFormat));
            setOpen(false);
          } else if (e.key === 'Escape') {
            setText(formatTimeKey(committedRef.current, timeFormat));
            setOpen(false);
          }
        }}
        onBlur={() => {
          // Let an option mousedown win before blur commits the typed text.
          setTimeout(() => {
            setOpen(false);
            if (document.activeElement !== inputRef.current) {
              commit(parseTimeInput(text, timeFormat));
            }
          }, 120);
        }}
      />
      {open && rect && (
        <DropdownList
          id={listId}
          anchorRect={rect}
          ariaLabel={t('calendar.editor.optionsFor', { label: ariaLabel })}
          onRequestClose={() => setOpen(false)}
          minWidth={startMinutes != null ? 170 : 130}
          items={visible.map((o) => ({
            key: o.key,
            label: formatTimeKey(o.key, timeFormat),
            hint: startMinutes != null ? durationHint(startMinutes, o.minutes) : null,
            selected: o.key === committedRef.current,
            onPick: () => {
              commit(o.key);
              setOpen(false);
            },
          }))}
        />
      )}
    </>
  );
}
